/**
 * 全域影片（船上 `assets/video/`）——小獅船長那幾支，全船共用。
 *
 * 這一頁刻意做成**固定格子**而不是檔案總管：路徑與檔名寫死在 `roiN.txt` 的
 * avatars 區塊裡，船機讀檔時才把 `{lang}` 換掉。既然路徑不是資料，就沒有讓人去
 * 管它的道理——每一格只問「這支放哪個影片」，其餘由後端算。
 *
 * 素材沿用媒體庫的挑選器（含預覽），所以可以先播一次再決定掛哪一支——這幾支全是
 * 影片，靠檔名根本分不出來。
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../components/Button';
import { MediaPicker } from '../components/MediaPicker';
import {
  clearDisplayGlobalMedia,
  listDisplayGlobalMedia,
  setDisplayGlobalMedia,
  type DisplayGlobalMediaEntry,
} from '../lib/api-client';

const LOCALE_LABEL: Record<string, string> = { zh: '繁中', en: '英文', jp: '日文' };

function formatMB(bytes: string): string {
  return `${(Number(bytes) / 1024 / 1024).toFixed(2)} MB`;
}

export function DisplayGlobalMediaPage(): JSX.Element {
  const qc = useQueryClient();
  const [picking, setPicking] = useState<DisplayGlobalMediaEntry | null>(null);

  const query = useQuery({
    queryKey: ['display.global-media'],
    queryFn: listDisplayGlobalMedia,
  });

  const onSettled = (rows: DisplayGlobalMediaEntry[]): void => {
    qc.setQueryData(['display.global-media'], rows);
    setPicking(null);
  };

  const setMutation = useMutation({
    mutationFn: (v: { slot: string; locale: string; mediaId: string }) =>
      setDisplayGlobalMedia(v.slot, v.locale, v.mediaId),
    onSuccess: onSettled,
  });

  const clearMutation = useMutation({
    mutationFn: (v: { slot: string; locale: string }) => clearDisplayGlobalMedia(v.slot, v.locale),
    onSuccess: onSettled,
  });

  const entries = query.data ?? [];
  const missing = entries.filter((e) => e.media === null);

  // 同一個插槽的三個語系排在一起，比一列列平鋪好認
  const slots = [...new Set(entries.map((e) => e.slot))];

  return (
    <div>
      <div className="page-head">
        <h1 className="page-head__title">全域影片</h1>
      </div>

      <div
        style={{
          background: '#eff6ff',
          border: '1px solid #bfdbfe',
          borderRadius: 6,
          padding: 12,
          margin: '12px 0',
          fontSize: 13,
          maxWidth: 900,
        }}
      >
        這幾支影片<strong>全船共用</strong>，不屬於任何景點，發布時會放到船上的{' '}
        <code>assets/video/</code>。每個景點的 <code>roiN.txt</code> 都指向它們，
        <strong>路徑與檔名是韌體寫死的</strong>，所以這裡只需要選素材。
        <br />
        開場與景點提示動畫的路徑帶 <code>{'{lang}'}</code>，三種語系各一支；
        小獅船長與觸發動畫不分語系，各只有一支。
      </div>

      {query.isLoading && <div>載入中…</div>}
      {query.error && <div className="error-banner">{(query.error as Error).message}</div>}
      {setMutation.error && (
        <div className="error-banner">{(setMutation.error as Error).message}</div>
      )}

      {missing.length > 0 && (
        <div
          style={{
            background: '#fffbeb',
            border: '1px solid #fcd34d',
            borderRadius: 6,
            padding: 12,
            margin: '12px 0',
            fontSize: 13,
            maxWidth: 900,
          }}
        >
          ⚠ 還有 {missing.length} 格未設定。發布不會被擋下，但 <code>roiN.txt</code>{' '}
          仍會指向這些路徑，船機屆時會找不到檔：
          <div style={{ fontFamily: 'monospace', fontSize: 12, marginTop: 4 }}>
            {missing.map((e) => e.target_path).join('、')}
          </div>
        </div>
      )}

      {slots.map((slot) => {
        const group = entries.filter((e) => e.slot === slot);
        const first = group[0]!;
        return (
          <fieldset
            key={slot}
            style={{
              border: '1px solid #e5e7eb',
              borderRadius: 6,
              padding: 12,
              margin: '12px 0',
              maxWidth: 900,
            }}
          >
            <legend style={{ fontSize: 13, fontWeight: 600 }}>
              {first.label}
              <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: 8, fontSize: 12 }}>
                roiN.txt → avatars.{first.avatar_key}
              </span>
            </legend>

            {group.map((e) => (
              <div
                key={`${e.slot}|${e.locale}`}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 10,
                  padding: '6px 0',
                  borderBottom: '1px solid #f3f4f6',
                }}
              >
                <span style={{ width: 48, fontSize: 13 }}>
                  {e.locale ? LOCALE_LABEL[e.locale] : '共用'}
                </span>
                <code style={{ fontSize: 11, color: '#6b7280', width: 240 }}>{e.target_path}</code>
                <span style={{ flex: 1, minWidth: 0, fontSize: 13 }}>
                  {e.media ? (
                    <>
                      <span style={{ wordBreak: 'break-all' }}>{e.media.filename}</span>
                      <span style={{ color: '#9ca3af', marginLeft: 6, fontSize: 11 }}>
                        {formatMB(e.media.size_bytes)}
                      </span>
                    </>
                  ) : (
                    <span style={{ color: '#9ca3af' }}>尚未設定</span>
                  )}
                </span>
                <Button
                  variant="secondary"
                  onClick={() => setPicking(e)}
                  disabled={setMutation.isPending}
                >
                  {e.media ? '更換' : '＋ 選擇影片'}
                </Button>
                {e.media && (
                  <Button
                    variant="ghost"
                    onClick={() => clearMutation.mutate({ slot: e.slot, locale: e.locale })}
                    disabled={clearMutation.isPending}
                  >
                    清空
                  </Button>
                )}
              </div>
            ))}
          </fieldset>
        );
      })}

      {picking && (
        <MediaPicker
          open
          title={`選擇「${picking.label}${picking.locale ? `／${LOCALE_LABEL[picking.locale]}` : ''}」影片 → ${picking.target_path}`}
          mediaType="video"
          acceptMimes={['video/mp4']}
          multiple={false}
          initialSelected={picking.media ? [picking.media.id] : []}
          onConfirm={(ids) => {
            const mediaId = ids[0];
            if (!mediaId) {
              setPicking(null);
              return;
            }
            setMutation.mutate({ slot: picking.slot, locale: picking.locale, mediaId });
          }}
          onClose={() => setPicking(null)}
        />
      )}
    </div>
  );
}
